import React from 'react';
import ReactDOM from 'react-dom';
const { Icon } = window;
/* ============================================================
   BOOKER — Diálogos globais (substituem window.confirm/alert nativos)
     window.confirmar(texto | {titulo, mensagem, perigoso, confirmar, cancelar}) -> Promise<bool>
     window.avisar   (texto | {titulo, mensagem, tom})                            -> Promise<void>
   ============================================================ */
var { useState, useEffect, useRef } = React;

const reduz = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };

function tomDe(s) {
  const t = (s || '').toLowerCase();
  if (/falha|erro|inválid|não foi|não consegu|não identifiqu|escolha|selecione/.test(t)) return 'erro';
  if (/salv|aplicad|import|conclu|sucesso|vinculad|removid|exclu[ií]d/.test(t)) return 'ok';
  return 'info';
}
function perigosoDe(s) { return /exclu|permanente|apag|remov|desvincul/i.test(s || ''); }
function labelOk(s, perigoso) {
  if (/exclu/i.test(s)) return 'Excluir';
  if (/desvincul/i.test(s)) return 'Desvincular';
  if (/aplicar/i.test(s)) return 'Aplicar';
  return perigoso ? 'Confirmar' : 'Confirmar';
}

const TOM = {
  erro: { cor: 'var(--danger)', bg: 'var(--danger-bg)', icone: 'alert' },
  ok: { cor: 'var(--ok)', bg: 'var(--ok-bg)', icone: 'check' },
  info: { cor: 'var(--booker-orange)', bg: 'var(--warn-bg)', icone: 'alert' },
};

function build(tipo, o, resolve) {
  if (o == null || typeof o === 'string') o = { mensagem: o || '' };
  if (tipo === 'confirm') {
    const perigoso = o.perigoso != null ? o.perigoso : perigosoDe(o.mensagem || o.titulo);
    return {
      tipo, resolve, perigoso,
      titulo: o.titulo || (/exclu/i.test(o.mensagem || '') ? 'Confirmar exclusão' : 'Confirmar'),
      mensagem: o.mensagem || '',
      confirmar: o.confirmar || labelOk(o.mensagem || '', perigoso),
      cancelar: o.cancelar || 'Cancelar',
    };
  }
  const tom = o.tom || tomDe(o.mensagem || o.titulo);
  return {
    tipo, resolve, tom,
    titulo: o.titulo || (tom === 'erro' ? 'Algo deu errado' : tom === 'ok' ? 'Pronto' : 'Aviso'),
    mensagem: o.mensagem || '', confirmar: o.confirmar || 'Entendi',
  };
}

function Dialogs() {
  const [d, setD] = useState(null);
  const okRef = useRef(null), cancelRef = useRef(null);

  useEffect(() => {
    window.confirmar = (o) => new Promise((res) => setD(build('confirm', o, res)));
    window.avisar = (o) => new Promise((res) => setD(build('alert', o, res)));
  }, []);

  function fechar(val) { setD((cur) => { if (cur) cur.resolve(val); return null; }); }

  useEffect(() => {
    if (!d) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); fechar(d.tipo === 'confirm' ? false : undefined); }
      else if (e.key === 'Enter') { e.preventDefault(); fechar(d.tipo === 'confirm' ? true : undefined); }
    };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => { (d.tipo === 'confirm' && d.perigoso ? cancelRef : okRef).current?.focus(); }, 50);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [d]);

  if (!d) return null;
  const tom = d.tipo === 'confirm' ? (d.perigoso ? TOM.erro : TOM.info) : TOM[d.tom] || TOM.info;
  const anim = reduz();
  const okBg = d.tipo === 'confirm' && d.perigoso ? 'var(--danger)' : (d.tipo === 'alert' ? tom.cor : 'var(--booker-orange)');

  const btnBase = { padding: '10px 18px', borderRadius: 'var(--r-sm)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', outline: 'none', transition: 'filter .15s, box-shadow .15s' };
  const btnOk = { ...btnBase, border: '1px solid transparent', background: okBg, color: '#fff' };
  const btnCancel = { ...btnBase, border: '1px solid var(--border-strong)', background: 'var(--surface-2)', color: 'var(--text-strong)' };

  return ReactDOM.createPortal(
    <div
      role="presentation"
      onClick={() => fechar(d.tipo === 'confirm' ? false : undefined)}
      style={{
        position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, background: 'rgba(16,16,20,.62)', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)',
        animation: anim ? 'none' : 'fadeIn .18s ease both',
      }}
    >
      <div
        role="alertdialog" aria-modal="true" aria-labelledby="dlg-tit" aria-describedby="dlg-msg"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 440, background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
          animation: anim ? 'none' : 'popIn .22s cubic-bezier(.16,1,.3,1) both',
        }}
      >
        <div style={{ padding: '24px 24px 20px', display: 'flex', gap: 16 }}>
          <span style={{ width: 46, height: 46, borderRadius: 13, background: tom.bg, color: tom.cor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name={tom.icone} size={23} />
          </span>
          <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
            <h3 id="dlg-tit" style={{ margin: 0, fontSize: 17, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-.01em', textWrap: 'balance' }}>{d.titulo}</h3>
            {d.mensagem && <p id="dlg-msg" style={{ margin: '7px 0 0', fontSize: 13.5, lineHeight: 1.5, color: 'var(--text)', whiteSpace: 'pre-line' }}>{d.mensagem}</p>}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '0 24px 22px' }}>
          {d.tipo === 'confirm' && (
            <button ref={cancelRef} onClick={() => fechar(false)} style={btnCancel}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-inset)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--surface-2)')}>{d.cancelar}</button>
          )}
          <button ref={okRef} onClick={() => fechar(d.tipo === 'confirm' ? true : undefined)} style={btnOk}
            onMouseEnter={(e) => (e.currentTarget.style.filter = 'brightness(1.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.filter = 'none')}>{d.confirmar}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

Object.assign(window, { Dialogs });
